import { inject } from "vue";
import axios, { AxiosError } from "axios";
import { VueCookies } from "vue-cookies";
import { useToast } from "vue-toastification";
import {
  Configuration,
  UserApiFactory,
  ConfigApiFactory,
  JobApiFactory,
  ResultsApiFactory,
} from "@/generated/api/";
import { useBackendStore } from "@/stores";

const toast = useToast();

const webSocketUrl = (path: string) => {
  const proto = window.location.protocol == "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws/${path}`;
};

interface WebSocketEvents {
  open: (e: Event) => void;
  close: (e: CloseEvent) => void;
  error: (e: Event) => void;
  message: (e: MessageEvent) => void;
}

const apiWebSocket = (path: string, events: Partial<WebSocketEvents>) => {
  const url = webSocketUrl(path);
  console.info(`WebSocket ${url}`);
  const ws = new WebSocket(url);

  if (events.open) ws.addEventListener("open", events.open);
  if (events.close) ws.addEventListener("close", events.close);
  if (events.error) ws.addEventListener("error", events.error);
  if (events.message) ws.addEventListener("message", events.message);

  return ws;
};

export const useApi = () => {
  const $cookies = inject<VueCookies>("$cookies") as VueCookies;
  const csrfToken = $cookies.get("csrftoken");

  const axiosInstance = axios.create({
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
    },
  });

  const cfg = new Configuration();
  const args = [cfg, window.location.origin, axiosInstance] as const;
  return {
    ...UserApiFactory(...args),
    ...ResultsApiFactory(...args),
    ...JobApiFactory(...args),
    ...ConfigApiFactory(...args),

    openBuildJobWebSocket(
      id: number,
      events: Partial<WebSocketEvents>
    ): WebSocket {
      return apiWebSocket(`job/${id}/`, events);
    },
  };
};

interface ApiErrorDetail {
  detail: string;
}

const hasDetail = (r: unknown): r is ApiErrorDetail =>
  typeof r == "object" && r != null && "detail" in r;

const formatTime = (n: number, unit: string) => {
  // Handle only seconds as api seems to always show values in seconds
  if (!unit.startsWith("second")) return `${n} ${unit}`;
  const minute = 60;
  const hour = 60 * minute;
  const day = 24 * hour;
  let msg = "";

  const days = Math.floor(n / day);
  if (days > 0) msg = msg + ` ${days}d`;
  n = n % day;

  const hours = Math.floor(n / hour);
  if (days > 0 || hours > 0) msg = msg + ` ${hours}h`;
  n = n % hour;

  const minutes = Math.floor(n / minute);
  if (days > 0 || hours > 0 || minutes > 0) msg = msg + ` ${minutes}m`;
  n = n % minute;

  return (msg + ` ${n}s`).trim();
};

export const handleApiError = (error: AxiosError) => {
  const response = error.response;
  const too_many_requests = response?.status == 429;
  if (too_many_requests) {
    // Error due to API throttling
    // If throttling includes time then show it
    const time =
      hasDetail(response.data) &&
      response.data.detail.match(/Expected available in (\d+) (\S+)/);
    const timeMsg = time
      ? `Wait ${formatTime(Number(time[1]), time[2])}`
      : "Please wait";

    // Suggest logging in for anonymous users
    const backend = useBackendStore();
    const loginMsg = backend.userLoggedIn ? "" : " or log in";

    toast.error(`Too many requests. ${timeMsg}${loginMsg}.`);
  } else {
    toast.error("Communication with server failed.");
  }
  throw error;
};
